import { combineEpics, ofType } from 'redux-observable';
import { mapTo } from 'rxjs/operators';

import requestsStatuses from 'modules/common/constants/requestsStatuses';
import { LOGOUT } from 'modules/auth/actions/logout';

import { getAppointmentsByQueueType } from 'modules/queues/common/selectors/appointmentsByQueueType';
import { fetchAppointmentsListIdsAndCursor } from 'modules/queues/common/actions/fetchAppointmentsByQueueType';
import { ARCHIVED, UNENFORCED } from 'modules/queues/constants/queueEnforcementTypes';
import { FETCH_APPOINTMENTS } from 'modules/queues/Schedule/actions/fetchAppointments';
import { FETCH_ARCHIVED_APPOINTMENTS } from 'modules/queues/Archive/actions/fetchArchivedAppointments';

import { fetchAppointmentById } from '../Schedule/actions/fetchAppointmentById';
import {
  updateExistingAppointment,
  addAppointmentToList,
  removeAppointmentFromList,
} from './actions/appointmentsListActions';


class AppointmentsUpdatesManager {
  constructor() {
    this.updatesStopped = false;
    this.updateInProgress = false;
    this.appointmentsToUpdate = [];

    this.startUpdates = action$ =>
      action$.pipe(
        ofType(
          FETCH_APPOINTMENTS.success,
          FETCH_ARCHIVED_APPOINTMENTS.success,
        ),
        mapTo(dispatch => {
          this.updatesStopped = false;
          dispatch(this.runUpdates());
        }),
      );

    this.stopUpdates = action$ =>
      action$.pipe(
        ofType(
          FETCH_APPOINTMENTS.pending,
          FETCH_ARCHIVED_APPOINTMENTS.pending,
          LOGOUT.success,
          LOGOUT.failure,
        ),
        mapTo(() => {
          this.updatesStopped = true;
          this.appointmentsToUpdate = [];
        }),
      );
  }


  addAppointmentToUpdate = appointmentId => (dispatch, getState) => {
    const { updateInProgress, updatesStopped, appointmentsToUpdate, runUpdates } = this;
    const { status: appointmentFetchStatus } = getAppointmentsByQueueType(getState());

    if (!appointmentsToUpdate.includes(appointmentId)) appointmentsToUpdate.push(appointmentId);

    // when list started fetching or previous update in progress or appts. list is fetching - delay updates
    if (updatesStopped || updateInProgress || appointmentFetchStatus === requestsStatuses.pending) return;
    dispatch(runUpdates());
  };

  runUpdates = () => (dispatch, getState) => {
    if (this.updatesStopped || this.appointmentsToUpdate.length === 0) return;

    const appointmentId = this.appointmentsToUpdate.shift();
    this.updateInProgress = true;

    dispatch(fetchAppointmentsListIdsAndCursor())
      .then(appointmentsIdsWithCursor => {
        if (this.updatesStopped) return;

        const state = getState();
        const listQueueType = state.queues.common.appointmentInfo.isArchiveOpened ? ARCHIVED : UNENFORCED;
        const appointments = getAppointmentsByQueueType(state).byId;
        const currentSelectedAppointmentId = state.queues.common.appointmentInfo.appointmentId;

        if (!appointmentsIdsWithCursor && currentSelectedAppointmentId === appointmentId) {
          return dispatch(fetchAppointmentById(appointmentId, true));
        }

        const currentIndex = appointments.findIndex(_appointmentId => _appointmentId === appointmentId);
        const nextIndex = appointmentsIdsWithCursor.findIndex(appt => appt.appointmentId === appointmentId);

        switch (true) {
          case (currentIndex >= 0 && nextIndex === -1):
            if (currentSelectedAppointmentId === appointmentId) {
              dispatch(fetchAppointmentById(appointmentId, true));
            }

            if (this.updatesStopped) return;
            return dispatch(removeAppointmentFromList(appointmentId, listQueueType));

          case (currentIndex === -1 && nextIndex >= 0):
            return dispatch(fetchAppointmentById(appointmentId, true))
              .then(({ appointment }) => {
                if (this.updatesStopped || !appointment) return;

                return dispatch(addAppointmentToList(appointment, nextIndex, listQueueType));
              });

          case (currentIndex >= 0 && nextIndex >= 0):
            return dispatch(fetchAppointmentById(appointmentId, true))
              .then(({ appointment }) => {
                if (this.updatesStopped || !appointment) return;

                return dispatch(updateExistingAppointment(appointment, currentIndex, nextIndex, listQueueType));
              });
          default:
            break;
        }
      })
      .finally(() => {
        this.updateInProgress = false;
        dispatch(this.runUpdates());
      });
  }
}

const AppointmentsUpdatesManagerInstance = new AppointmentsUpdatesManager();
const { startUpdates, stopUpdates, addAppointmentToUpdate } = AppointmentsUpdatesManagerInstance;

export const appointmentsUpdatesEpics = combineEpics(
  startUpdates,
  stopUpdates,
);

export { addAppointmentToUpdate };
